import React, { FC } from 'react';
import { useVitalMetric } from '@funtech-int/yango-shared';

import { PlayerSkinErrorBoundary } from '@/scenarios/legacy-player/components/PlayerSkin/PlayerSkinErrorBoundary';
import { playerChannelFirstFrame } from '@/scenarios/player/lib/metrics';

import { Player, type PlayerConfigProps } from '../../../Player/Player';
import { PlayerTracker } from '../../../feature-player/ui/player-tracker/component';
import { SportPlayerController } from '../SportPlayerController/SportPlayerController';
import { SportPlayerView } from '../SportPlayerView/SportPlayerView';
import { SportPlayerTracker } from '../SportPlayerTracker/SportPlayerTracker';

interface SportPlayerProps {
    sportLiveContentId: string;
    /** Default: true */
    hasSkin?: boolean;
    onStart?: (contentId: string) => void;
    onClose?: () => void;
    onDurationChange?: (duration: number) => void;
    isPromo?: boolean;
}

export const SportLivePlayer: FC<SportPlayerProps> = ({
    onStart,
    sportLiveContentId,
    onClose,
    hasSkin = true,
    onDurationChange = (): void => undefined,
}) => {
    const firstFrameDuration = useVitalMetric(playerChannelFirstFrame);

    const handleStart: PlayerConfigProps['onStart'] = (contentId) => {
        firstFrameDuration.end();

        onStart?.(contentId);
    };

    if (!sportLiveContentId) {
        return null;
    }

    return (
        <PlayerSkinErrorBoundary>
            {/* eslint-disable-next-line react/no-unused-prop-types */}
            {({ forceDefaultSkin }: { forceDefaultSkin: boolean }) => (
                <Player
                    contentId={sportLiveContentId}
                    onStart={handleStart}
                    onClose={onClose}
                    onBeforeInit={firstFrameDuration.begin}
                    renderPlayer={({
                        contentId,
                        psid,
                        onStartCallback,
                        handleError,
                        handleFinish,
                        onClose = (): void => undefined,
                    }) => (
                        <PlayerTracker>
                            <SportPlayerController
                                onContentImpression={onStartCallback}
                                onFinish={handleFinish}
                                onError={handleError}
                                onDurationChanged={onDurationChange}
                            />
                            <SportPlayerTracker contentId={contentId} psid={psid} />
                            <SportPlayerView
                                contentId={contentId}
                                onClose={onClose}
                                psid={psid}
                                withSkin={Boolean(hasSkin) && !forceDefaultSkin}
                            />
                        </PlayerTracker>
                    )}
                />
            )}
        </PlayerSkinErrorBoundary>
    );
};
